import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Mail, Camera, Loader2, Save, ShieldCheck, Pencil, ArrowLeft } from "lucide-react";

export default function IdentityPage() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [profile, setProfile] = useState({
    legal_name: "",
    date_of_birth: "",
    email: "",
    gender: "",
    photo_url: ""
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from("identity")
          .select("*")
          .eq("user_id", user.id)
          .maybeSingle();
        
        if (data) {
          setProfile(data);
          setIsEditing(false); // Found record: show view mode
        } else {
          setIsEditing(true); // No record: show edit mode
        }
      }
    } catch (err) {
      console.error("Error fetching identity:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated session found.");
      
      const { error } = await supabase
        .from("identity")
        .upsert({ 
          user_id: user.id,
          ...profile,
          updated_at: new Date().toISOString() 
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      toast({ 
        title: "Identity Synchronized", 
        description: "Your passport records have been committed to the registry." 
      });
      setIsEditing(false);
    } catch (error: any) {
      toast({ 
        title: "Update Failed", 
        description: error.message, 
        variant: "destructive" 
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
      <Loader2 className="animate-spin w-6 h-6 text-black/20" />
      <p className="font-mono text-[10px] uppercase tracking-widest text-black/40">Accessing Vault...</p>
    </div>
  );

  return (
    <div className="p-8 max-w-2xl mx-auto space-y-12 animate-in fade-in duration-700">
      <header className="flex justify-between items-end border-b border-black/10 pb-8">
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.4em] text-black/40 font-mono font-bold">Registry // Identity</p>
          <h1 className="font-serif text-4xl italic tracking-tighter text-black">Vital Records</h1>
        </div>
        <div className="flex flex-col items-end gap-3">
          <span className="text-[9px] font-mono text-emerald-600 bg-emerald-50 px-2 py-0.5 border border-emerald-100 uppercase tracking-tighter flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Active_Node
          </span>
          {!isEditing && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsEditing(true)}
              className="font-mono text-[10px] uppercase tracking-widest gap-2 h-7 rounded-none border border-black/5 hover:bg-black hover:text-white"
            >
              <Pencil className="w-3 h-3" /> Edit Profile
            </Button>
          )}
        </div>
      </header>

      {isEditing ? (
        /* EDIT VIEW */
        <div className="space-y-10 animate-in slide-in-from-bottom-2 duration-500">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-8 bg-zinc-50/50 p-6 border border-black/[0.03]">
            <div className="w-32 h-40 bg-white border border-black/10 flex items-center justify-center overflow-hidden relative shadow-sm shrink-0">
              {profile.photo_url ? (
                <img src={profile.photo_url} alt="Identity" className="w-full h-full object-cover" />
              ) : (
                <Camera className="w-8 h-8 text-black/5" />
              )}
            </div>
            <div className="flex-1 space-y-3 w-full">
              <Label className="font-serif italic text-sm">Passport Image (Portrait URL)</Label>
              <Input 
                placeholder="Source URL..." 
                className="rounded-none border-black/10 font-mono text-[10px] h-10 focus-visible:ring-black bg-white"
                value={profile.photo_url}
                onChange={(e) => setProfile({...profile, photo_url: e.target.value})}
              />
              <p className="text-[9px] text-black/30 font-mono uppercase tracking-tight">Requirement: Front-facing portrait view.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
            <div className="space-y-2">
              <Label className="font-mono text-[11px] uppercase tracking-widest text-black/40">Legal Full Name</Label>
              <Input 
                className="rounded-none border-black/10 font-mono text-[11px] h-12 focus-visible:ring-black" 
                value={profile.legal_name}
                onChange={(e) => setProfile({...profile, legal_name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-[11px] uppercase tracking-widest text-black/40">Date of Birth</Label>
              <Input 
                type="date" 
                className="rounded-none border-black/10 font-mono h-12 text-[12px] uppercase focus-visible:ring-black" 
                value={profile.date_of_birth}
                onChange={(e) => setProfile({...profile, date_of_birth: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-[11px] uppercase tracking-widest text-black/40">Biological Gender</Label>
              <Select value={profile.gender} onValueChange={(val) => setProfile({...profile, gender: val})}>
                <SelectTrigger className="rounded-none border-black/10 h-12 font-mono text-sm">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="rounded-none font-mono">
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-[11px] uppercase tracking-widest text-black/40">Passport Email</Label>
              <Input  
                className="rounded-none border-black/10 font-mono text-[11px] h-12 focus-visible:ring-black" 
                value={profile.email}
                onChange={(e) => setProfile({...profile, email: e.target.value})}
              />
            </div>
          </div>

          <div className="pt-8 flex flex-col md:flex-row gap-4">
            <Button 
              variant="outline"
              onClick={() => setIsEditing(false)}
              className="flex-1 rounded-none h-14 font-mono text-[10px] uppercase tracking-widest border-black/10"
              disabled={!profile.legal_name}
            >
              <ArrowLeft className="w-3 h-3 mr-2" /> Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="flex-[2] bg-black text-white rounded-none h-14 font-serif italic text-xl hover:bg-zinc-900"
            >
              {saving ? <Loader2 className="animate-spin w-5 h-5" /> : "Save Identity"}
            </Button>
          </div>
        </div>
      ) : (
        /* READ-ONLY VIEW */
        <div className="space-y-12 animate-in fade-in slide-in-from-top-1 duration-500">
          <div className="flex flex-col md:flex-row gap-12 items-start">
            <div className="w-48 h-60 bg-zinc-50 border-4 border-double border-black/10 p-2 shadow-lg shrink-0">
              {/* Grayscale and contrast removed for natural color view */}
              <div className="w-full h-full border border-black/5 overflow-hidden bg-white flex items-center justify-center">
                {profile.photo_url ? (
                  <img src={profile.photo_url} alt="Passport" className="w-full h-full object-cover" />
                ) : (
                  <Camera className="w-12 h-12 text-black/5" />
                )}
              </div>
            </div>

            <div className="flex-1 grid grid-cols-1 gap-y-10">
              <div className="border-l border-black/5 pl-6 space-y-1">
                <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-black/30">Name</p>
                <p className="font-serif text-2xl uppercase tracking-tighter text-black">{profile.legal_name || "NOT_RECORDED"}</p>
              </div>

              <div className="grid grid-cols-2 gap-8">
                <div className="border-l border-black/5 pl-6 space-y-1">
                  <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-black/30">DOB</p>
                  <p className="font-mono text-xs uppercase text-black">{profile.date_of_birth || "PNDG"}</p>
                </div>
                <div className="border-l border-black/5 pl-6 space-y-1">
                  <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-black/30">Gender</p>
                  <p className="font-mono text-xs uppercase text-black">{profile.gender || "PNDG"}</p>
                </div>
              </div>

              <div className="border-l border-black/5 pl-6 space-y-1">
                <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-black/30">Contact Protocol</p>
                <div className="flex items-center gap-2">
                  <Mail className="w-3 h-3 text-black/20" />
                  <p className="font-mono text-xs text-black/60">{profile.email || "NO_LINK_ESTABLISHED"}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-zinc-50 p-6 border border-black/[0.03]">
            <p className="font-serif italic text-sm text-black/40 leading-relaxed text-center">
              This node's identity is cryptographically bound to the Network Protocol. Changes to vital records require manual synchronization.
            </p>
          </div>
        </div>
      )}

      <footer className="mt-20 pt-8 border-t border-black/[0.03]">
        <p className="text-center font-mono text-[8px] uppercase tracking-[0.5em] text-black/20">
          Network Protocol Internal Registry — V0.1
        </p>
      </footer>
    </div>
  );
}